import React, { memo, useMemo, useState, useCallback, useRef, useEffect } from 'react';
import { Search, ChevronDown, ChevronUp, Globe, X } from 'lucide-react';
import { CountryFlag } from '../../CountryFlag';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { useClickOutside } from '@/hooks/useClickOutside';
import { useTranslation } from '@/hooks/useTranslation';

/**
 * Base language interface for all language selectors
 */
export interface Language {
  code: string;
  name: string;
  nativeName: string;
  flag?: string;
  region?: string;
  nllb_code?: string;
  isTop?: boolean;
}

// Region configuration moved to separate constants to avoid fast refresh warnings
const REGION_ICONS_MAP: Record<string, string> = {
  Global: '🌍',
  Europe: '🌍',
  Asia: '🌏',
  Africa: '🌍',
  'North America': '🌎',
  'South America': '🌎',
  Oceania: '🌏',
  'Middle East': '🌍',
  Caribbean: '🌴',
  Historical: '🏛️',
  Unknown: '🌐',
};

const REGION_ORDER_LIST = [
  'Global',
  'Europe',
  'Asia',
  'North America',
  'South America',
  'Africa',
  'Oceania',
  'Middle East',
  'Caribbean',
  'Historical',
  'Unknown',
];

/**
 * Quick action button configuration
 */
export interface QuickAction {
  id: string;
  label: string;
  description?: string;
  icon: React.ReactNode;
  codes: string[];
  className: string;
}

/**
 * Base language selector props
 */
export interface BaseLanguageSelectorProps {
  /** All available languages */
  languages: Language[];
  /** Selected language codes */
  values: string[];
  /** Callback when selection changes */
  onChange: (codes: string[]) => void;
  /** Whether the selector is disabled */
  disabled?: boolean;
  /** Additional CSS class */
  className?: string;
  /** Maximum number of selections (undefined = unlimited, 1 = single select) */
  maxSelection?: number;
  /** Show quick action buttons */
  showQuickActions?: boolean;
  /** Quick action buttons configuration */
  quickActions?: QuickAction[];
  /** Custom placeholder text */
  placeholder?: string;
  /** Search placeholder text */
  searchPlaceholder?: string;
  /** Footer description text */
  footerDescription?: string;
  /** Top languages to show in special group (codes) */
  topLanguageCodes?: string[];
  /** Label for top languages group */
  topLanguagesLabel?: string;
  /** Initially expanded regions */
  initialExpandedRegions?: string[];
  /** Test ID for the main button */
  testId?: string;
  /** Render custom language item content */
  renderLanguageItem?: (lang: Language, isSelected: boolean, rank?: number) => React.ReactNode;
  /** Show close button on mobile */
  showMobileClose?: boolean;
}

/**
 * BaseLanguageSelector - Composable base component for all language selectors
 *
 * Features:
 * - Search with name, native name, and code matching
 * - Region grouping with collapsible sections
 * - Optional top languages group
 * - Quick action buttons (select all, clear, etc.)
 * - Mobile-optimized fullscreen dropdown
 * - Keyboard navigation support
 * - Single or multi-select mode
 */
export const BaseLanguageSelector = memo(function BaseLanguageSelector({
  languages,
  values = [],
  onChange,
  disabled = false,
  className = '',
  maxSelection,
  showQuickActions = true,
  quickActions = [],
  placeholder,
  searchPlaceholder,
  footerDescription,
  topLanguageCodes = [],
  topLanguagesLabel = 'Top 20 Most Spoken',
  initialExpandedRegions = ['Top 20 Most Spoken', 'Europe', 'Asia'],
  testId = 'language-selector',
  renderLanguageItem,
  showMobileClose = true,
}: BaseLanguageSelectorProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedRegions, setExpandedRegions] = useState<Set<string>>(
    new Set([topLanguagesLabel, ...initialExpandedRegions])
  );
  const [dropdownPosition, setDropdownPosition] = useState<'bottom' | 'top'>('bottom');
  const [maxDropdownHeight, setMaxDropdownHeight] = useState<number>(350);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const { isMobile } = useBreakpoint();

  // Defensive check: ensure values is always an array (wrapped in useMemo for stable reference)
  const safeValues = useMemo(() => (Array.isArray(values) ? values : []), [values]);
  const isSingleSelect = maxSelection === 1;
  const selectedCount = safeValues.length;
  const isMaxReached =
    maxSelection !== undefined && selectedCount >= maxSelection && !isSingleSelect;

  // Close dropdown when clicking outside
  useClickOutside(dropdownRef, () => setIsOpen(false));

  // Close dropdown on ESC key (also returns focus to the trigger button — kept
  // separate from useClickOutside because it does more than a plain close).
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        buttonRef.current?.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  // Focus search when opening
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  // Elevate parent stacking context when dropdown is open
  // backdrop-filter on .config-section creates a stacking context that traps z-index,
  // causing the dropdown to appear behind sibling sections in the form
  useEffect(() => {
    if (!isOpen || !dropdownRef.current) return;

    const stackingParent = dropdownRef.current.closest('.config-section') as HTMLElement | null;
    if (!stackingParent) return;

    const prevZIndex = stackingParent.style.zIndex;
    const prevPosition = stackingParent.style.position;
    stackingParent.style.zIndex = '10';
    stackingParent.style.position = 'relative';

    return () => {
      stackingParent.style.zIndex = prevZIndex;
      stackingParent.style.position = prevPosition;
    };
  }, [isOpen]);

  // Calculate dropdown position based on viewport space
  useEffect(() => {
    if (isOpen && buttonRef.current && !isMobile) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - buttonRect.bottom - 16;
      const spaceAbove = buttonRect.top - 16;
      const minUsableHeight = 200;

      if (spaceBelow >= minUsableHeight) {
        setDropdownPosition('bottom');
        setMaxDropdownHeight(Math.min(spaceBelow, 400));
      } else if (spaceAbove > spaceBelow) {
        setDropdownPosition('top');
        setMaxDropdownHeight(Math.min(spaceAbove, 400));
      } else {
        setDropdownPosition('bottom');
        setMaxDropdownHeight(Math.max(spaceBelow, minUsableHeight));
      }
    }
  }, [isOpen, isMobile]);

  // Get top languages
  const topLanguages = useMemo(() => {
    if (topLanguageCodes.length === 0) return [];
    return topLanguageCodes
      .map((code) => languages.find((lang) => lang.code === code))
      .filter((lang): lang is Language => lang !== undefined)
      .map((lang) => ({ ...lang, isTop: true }));
  }, [languages, topLanguageCodes]);

  // Filter and group languages
  const { filteredLanguages, groupedLanguages } = useMemo(() => {
    const query = searchQuery.toLowerCase();

    // Filter based on search
    const filtered = searchQuery
      ? languages.filter(
          (lang) =>
            lang.name.toLowerCase().includes(query) ||
            lang.nativeName.toLowerCase().includes(query) ||
            lang.code.toLowerCase().includes(query) ||
            (lang.nllb_code && lang.nllb_code.toLowerCase().includes(query))
        )
      : languages;

    // Group by region
    const grouped: Record<string, Language[]> = {};

    // Add top languages as first group if configured
    if (topLanguageCodes.length > 0) {
      const topFiltered = searchQuery
        ? topLanguages.filter(
            (lang) =>
              lang.name.toLowerCase().includes(query) ||
              lang.nativeName.toLowerCase().includes(query) ||
              lang.code.toLowerCase().includes(query)
          )
        : topLanguages;

      if (topFiltered.length > 0) {
        grouped[topLanguagesLabel] = topFiltered;
      }
    }

    // Add other languages grouped by region (excluding top languages)
    const topCodesSet = new Set(topLanguageCodes);
    const nonTopFiltered = filtered.filter((lang) => !topCodesSet.has(lang.code));

    nonTopFiltered.forEach((lang) => {
      const region = lang.region || 'Unknown';
      if (!grouped[region]) {
        grouped[region] = [];
      }
      grouped[region].push(lang);
    });

    // Sort languages within each group (except top group)
    Object.entries(grouped).forEach(([region, group]) => {
      if (region !== topLanguagesLabel) {
        group.sort((a, b) => a.name.localeCompare(b.name));
      }
    });

    return { filteredLanguages: filtered, groupedLanguages: grouped };
  }, [searchQuery, languages, topLanguages, topLanguageCodes, topLanguagesLabel]);

  // Toggle region expansion
  const toggleRegion = useCallback((region: string) => {
    setExpandedRegions((prev) => {
      const newExpanded = new Set(prev);
      if (newExpanded.has(region)) {
        newExpanded.delete(region);
      } else {
        newExpanded.add(region);
      }
      return newExpanded;
    });
  }, []);

  // Handle language selection
  const handleSelect = useCallback(
    (code: string) => {
      if (disabled) return;

      const currentSelection = [...safeValues];
      const index = currentSelection.indexOf(code);

      if (index >= 0) {
        // Removing existing selection
        if (isSingleSelect) {
          return; // Can't deselect in single-selection mode
        }
        currentSelection.splice(index, 1);
      } else {
        // Adding new selection
        if (isSingleSelect) {
          currentSelection.length = 0;
          currentSelection.push(code);
        } else if (maxSelection === undefined || currentSelection.length < maxSelection) {
          currentSelection.push(code);
        } else {
          return; // Max reached
        }
      }

      onChange(currentSelection);

      // Close dropdown after every selection so newly revealed content (e.g. per-language
      // voice config in AudioLanguageSelector) isn't covered by the still-open list. Bulk
      // selection is handled by Quick Actions (Top 20 / YouTube / All), not item-by-item.
      setIsOpen(false);
    },
    [disabled, safeValues, isSingleSelect, maxSelection, onChange]
  );

  // Handle quick action
  const handleQuickAction = useCallback(
    (codes: string[]) => {
      if (disabled) return;
      onChange(codes);
    },
    [disabled, onChange]
  );

  // Clear all selections
  const handleClearAll = useCallback(() => {
    if (!disabled) {
      onChange([]);
    }
  }, [disabled, onChange]);

  // Get selected languages for display
  const selectedLanguages = useMemo(
    () => languages.filter((lang) => safeValues.includes(lang.code)),
    [languages, safeValues]
  );

  // Default language item renderer
  const defaultRenderLanguageItem = useCallback(
    (lang: Language, isSelected: boolean, rank?: number) => (
      <>
        <div className="flex items-center space-x-3">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => handleSelect(lang.code)}
            disabled={disabled || (!isSelected && isMaxReached)}
            className="h-4 w-4 text-primary-500 border-white/20 rounded bg-black/20 focus:ring-primary-500"
            onClick={(e) => e.stopPropagation()}
            data-testid={`lang-${lang.code}`}
          />
          <CountryFlag code={lang.code} style={{ width: '24px', height: '24px' }} />
          <div className="text-left">
            <div className="text-sm font-medium text-white">
              {lang.name}
              {rank !== undefined && (
                <span className="ml-1 text-xs bg-yellow-500/20 text-yellow-400 px-1 rounded">
                  #{rank + 1}
                </span>
              )}
            </div>
            <div className="text-xs text-white/50">{lang.nativeName}</div>
          </div>
        </div>
        <span className="text-xs text-white/30 uppercase">{lang.code}</span>
      </>
    ),
    [disabled, handleSelect, isMaxReached]
  );

  const renderItem = renderLanguageItem || defaultRenderLanguageItem;

  // When searching, force every region open so matches in a collapsed region
  // are still visible (the filter already spans ALL languages). Without this,
  // searching e.g. "Portu" while "Europe" is collapsed shows nothing.
  const isRegionExpanded = useCallback(
    (region: string): boolean => (searchQuery ? true : expandedRegions.has(region)),
    [searchQuery, expandedRegions]
  );

  return (
    <div className={className}>
      {/* Quick action buttons */}
      {showQuickActions && !isSingleSelect && quickActions.length > 0 && (
        <div className="flex flex-col gap-2 mb-2">
          <div className="flex justify-end">
            <span className="text-xs text-white/40 whitespace-nowrap">
              {maxSelection && maxSelection > 100
                ? t('languageSelector.languagesSelected', { count: selectedCount })
                : maxSelection
                  ? t('languageSelector.selectedCount', {
                      selected: selectedCount,
                      total: maxSelection,
                    })
                  : t('languageSelector.languagesSelected', { count: selectedCount })}
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {quickActions.map((action) => (
              <div key={action.id} className="flex flex-col min-w-0">
                <button
                  type="button"
                  onClick={() => handleQuickAction(action.codes)}
                  disabled={
                    disabled || (maxSelection !== undefined && maxSelection < action.codes.length)
                  }
                  className={`px-3 py-2 text-xs rounded-md disabled:opacity-50 whitespace-nowrap flex items-center gap-1 ${action.className}`}
                >
                  {action.icon}
                  {action.label}
                </button>
                {action.description && (
                  <span className="text-[10px] text-white/40 mt-1 text-center">
                    {action.description}
                  </span>
                )}
              </div>
            ))}
            {selectedCount > 0 && (
              <button
                type="button"
                onClick={handleClearAll}
                disabled={disabled}
                className="px-3 py-2 text-xs rounded-md bg-red-500/10 text-red-400 border border-red-500/30 hover:bg-red-500/20 disabled:opacity-50 whitespace-nowrap self-start"
              >
                Clear
              </button>
            )}
          </div>
        </div>
      )}

      {/* Single selection mode hint */}
      {isSingleSelect && (
        <div className="flex items-center justify-end mb-2">
          <span className="text-xs text-white/40">
            {selectedCount > 0 ? 'Click another to replace' : 'Select one language'}
          </span>
        </div>
      )}

      {/* Language selector dropdown */}
      <div className="relative" ref={dropdownRef}>
        <button
          ref={buttonRef}
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          data-testid={testId}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-label={placeholder || 'Select language'}
          className={`w-full px-4 py-2 min-h-[44px] text-left bg-black/30 border border-white/10 rounded-lg shadow-sm hover:border-white/20 hover:bg-black/40 focus:outline-none focus:ring-1 focus:ring-primary-500 transition-all ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 flex-1">
              {selectedCount > 0 ? (
                <div className="flex items-center flex-wrap gap-1">
                  {selectedLanguages.slice(0, 3).map((lang) => (
                    <span
                      key={lang.code}
                      className="inline-flex items-center px-2 py-1 bg-primary-900/30 border border-primary-500/50 text-primary-200 text-xs rounded-md group"
                    >
                      <CountryFlag
                        code={lang.code}
                        className="mr-1 inline-block"
                        style={{ width: '16px', height: '16px' }}
                      />
                      {lang.name}
                      {!isSingleSelect && (
                        <span
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSelect(lang.code);
                          }}
                          className="ml-1 text-primary-400 hover:text-primary-200 cursor-pointer"
                          aria-label={`Remove ${lang.name}`}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.stopPropagation();
                              e.preventDefault();
                              handleSelect(lang.code);
                            }
                          }}
                        >
                          x
                        </span>
                      )}
                    </span>
                  ))}
                  {selectedCount > 3 && (
                    <span className="text-sm text-white/50 ml-1">
                      {t('languageSelector.moreLanguages', { count: selectedCount - 3 })}
                    </span>
                  )}
                </div>
              ) : (
                <span className="text-white/40 flex items-center">
                  <Globe className="w-4 h-4 mr-2" />
                  {placeholder ||
                    (isSingleSelect
                      ? t('languageSelector.selectLanguage')
                      : maxSelection
                        ? t('languageSelector.selectLanguagesWithMax', { max: maxSelection })
                        : t('languageSelector.selectLanguages'))}
                </span>
              )}
            </div>
            <ChevronDown
              className={`w-5 h-5 text-white/40 transition-transform ${isOpen ? 'rotate-180' : ''}`}
            />
          </div>
        </button>

        {/* Dropdown */}
        {isOpen && (
          <>
            {/* Mobile overlay backdrop */}
            {isMobile && (
              <div
                className="fixed inset-0 bg-black/50 z-40"
                onClick={() => setIsOpen(false)}
                aria-hidden="true"
              />
            )}
            <div
              className={`bg-zinc-900 border border-white/10 rounded-lg shadow-2xl overflow-hidden ${
                isMobile
                  ? 'dropdown-fullscreen-mobile'
                  : `absolute z-[60] w-full ${dropdownPosition === 'top' ? 'bottom-full mb-2' : 'top-full mt-2'}`
              }`}
              style={isMobile ? undefined : { maxHeight: maxDropdownHeight }}
            >
              {/* Header with search */}
              <div className="p-3 border-b border-white/10 flex items-center gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-white/40" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={
                      searchPlaceholder ||
                      t('languageSelector.searchPlaceholder', { count: languages.length })
                    }
                    className="w-full pl-10 pr-3 py-2 bg-black/20 border border-white/10 rounded focus:outline-none focus:ring-1 focus:ring-primary-500 text-sm text-white placeholder-white/30"
                  />
                </div>
                {isMobile && showMobileClose && (
                  <button
                    type="button"
                    onClick={() => setIsOpen(false)}
                    className="p-2 rounded-full hover:bg-white/10 transition-colors flex-shrink-0"
                    aria-label={t('common.close')}
                  >
                    <X className="w-5 h-5 text-white/50" />
                  </button>
                )}
              </div>

              {/* Language list */}
              <div
                className="overflow-y-auto scroll-smooth-touch dropdown-scroll-area"
                style={isMobile ? undefined : { maxHeight: maxDropdownHeight - 100 }}
              >
                {filteredLanguages.length === 0 ? (
                  <div className="p-4 text-center text-white/50">
                    {t('languageSelector.noLanguagesFound')}
                  </div>
                ) : (
                  Object.entries(groupedLanguages)
                    .filter(([, langs]) => langs.length > 0)
                    .sort(([a], [b]) => {
                      if (a === topLanguagesLabel) return -1;
                      if (b === topLanguagesLabel) return 1;
                      return REGION_ORDER_LIST.indexOf(a) - REGION_ORDER_LIST.indexOf(b);
                    })
                    .map(([region, langs]) => (
                      <div key={region} className="border-b border-white/5 last:border-0">
                        {/* Region header */}
                        <button
                          type="button"
                          onClick={() => toggleRegion(region)}
                          className="w-full px-3 py-2 flex items-center justify-between hover:bg-white/5 transition-colors"
                        >
                          <div className="flex items-center space-x-2">
                            <span className="text-lg">
                              {region === topLanguagesLabel
                                ? '⭐'
                                : REGION_ICONS_MAP[region] || '🌐'}
                            </span>
                            <span className="font-medium text-sm text-white/70">{region}</span>
                            <span className="text-xs text-white/40">({langs.length})</span>
                          </div>
                          {isRegionExpanded(region) ? (
                            <ChevronUp className="w-4 h-4 text-white/40" />
                          ) : (
                            <ChevronDown className="w-4 h-4 text-white/40" />
                          )}
                        </button>

                        {/* Language items */}
                        {isRegionExpanded(region) && (
                          <div className="py-1">
                            {langs.map((lang) => {
                              const isSelected = safeValues.includes(lang.code);
                              const isItemDisabled =
                                disabled || (!isSelected && isMaxReached && !isSingleSelect);
                              const rank =
                                region === topLanguagesLabel
                                  ? topLanguageCodes.indexOf(lang.code)
                                  : undefined;

                              return (
                                <button
                                  key={lang.code}
                                  type="button"
                                  onClick={() => handleSelect(lang.code)}
                                  disabled={isItemDisabled}
                                  className={`w-full px-3 py-2 flex items-center justify-between hover:bg-white/5 transition-colors group ${
                                    isItemDisabled ? 'opacity-50 cursor-not-allowed' : ''
                                  } ${isSelected ? 'bg-white/5' : ''}`}
                                >
                                  {renderItem(lang, isSelected, rank)}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    ))
                )}
              </div>

              {/* Footer */}
              {footerDescription && (
                <div className="px-3 py-2 border-t border-white/10 bg-black/20">
                  <div className="text-xs text-white/40 text-center">{footerDescription}</div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
});

export default BaseLanguageSelector;
